import {inject, Injectable} from "@angular/core";
import {BokkzWageDataService} from "./bokkz-wage-data-service";
import {
  BokkzSalarisClient,
  WerkgeverEntity,
  WerknemerEntity
} from "./api.bokkz.salaris";
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({providedIn: 'root'})
export class BokkzWageLogic {

  private bokkzClient = inject(BokkzSalarisClient);
  private dataService = inject(BokkzWageDataService);

  public postWerknemerEntity(clientId: string, wn: WerknemerEntity) {
    const requestedWerknemerId = wn?.id;

    this.bokkzClient.postOneEmployee(clientId, wn).subscribe((werknemer) => {
      const activeWerknemerId = this.dataService.werknemerEntity.value?.id;

      if (activeWerknemerId && requestedWerknemerId && activeWerknemerId !== requestedWerknemerId) {
        return;
      }

      if (activeWerknemerId && werknemer?.id && activeWerknemerId !== werknemer.id) {
        return;
      }

      this.dataService.updateWerknemer(werknemer);
    });
  }

  public postCalculationRequest(clientId: string, periode: string, jaar: string, werkgeverEntity: WerkgeverEntity) {
    this.bokkzClient.postOneCalculationRequest(clientId, periode, jaar, werkgeverEntity).subscribe((value) => {
      this.dataService.updateLoonJournaal(value);
    });
  }
}

function toDateOnly(value: unknown): Date | null {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? new Date(value) : new Date(value as string);

  if (isNaN(date.getTime())) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
}

/** * Valideert dat periode.begindatum niet vóór een referentiedatum ligt. * * Bedoeld voor FormGroups met: * - begindatum * - einddatum (optioneel) */
export function beginDatumNietVoorReferentieValidator(
  getReferentieDatum: () => Date | string | null | undefined,
  errorKey = 'begindatumVoorReferentieDatum'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const beginValue = control.get('begindatum')?.value;
    const referentieValue = getReferentieDatum();

    const beginDatum = toDateOnly(beginValue);
    const referentieDatum = toDateOnly(referentieValue);

    if (!beginDatum || !referentieDatum) {
      return null;
    }

    return beginDatum < referentieDatum
      ? {
        [errorKey]: {
          begindatum: beginValue,
          referentieDatum: referentieValue,
        },
      }
      : null;
  };
}

/** * Valideert dat einddatum niet vóór begindatum ligt. * Deze kun je ook centraliseren, zodat je bestaande dubbele code opruimt. */
export function eindDatumNietVoorBeginDatumValidator(
  errorKey = 'einddatumVoorBegindatum'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const beginValue = control.get('begindatum')?.value;
    const eindValue = control.get('einddatum')?.value;

    const beginDatum = toDateOnly(beginValue);
    const eindDatum = toDateOnly(eindValue);

    if (!beginDatum || !eindDatum) {
      return null;
    }

    return eindDatum < beginDatum
      ? {
        [errorKey]: {
          begindatum: beginValue,
          einddatum: eindValue,
        },
      }
      : null;
  };
}

export function beginDatumNietVoorDatumInDienstValidator(
  getDatumInDienst: () => Date | string | null | undefined
): ValidatorFn {
  return beginDatumNietVoorReferentieValidator(
    getDatumInDienst,
    'begindatumVoorDatumInDienst'
  );
}

export function datumNietVoorAndereDatumValidator(
  eerdereDatumControlName: string,
  latereDatumControlName: string,
  errorKey = 'datumVoorAndereDatum'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const eerdereDatum = toDateOnly(control.get(eerdereDatumControlName)?.value);
    const latereDatum = toDateOnly(control.get(latereDatumControlName)?.value);

    if (!eerdereDatum || !latereDatum) {
      return null;
    }

    return latereDatum < eerdereDatum
      ? {
        [errorKey]: {
          eerdereDatumControlName,
          latereDatumControlName,
        },
      }
      : null;
  };
}

export function datumUitDienstNietVoorDatumInDienstValidator(): ValidatorFn {
  return datumNietVoorAndereDatumValidator(
    'datumInDienst',
    'datumUitDienst',
    'datumUitDienstVoorDatumInDienst'
  );
}

